/**
 * Terminal — Terminal web remoto para máquinas conectadas via Runner V3
 *
 * Permite executar comandos na máquina remotamente com polling bidirecional.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Textarea } from './ui';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

interface TerminalCommand {
  id: string;
  command: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  output: string;
  exit_code?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

// ──────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────

const POLL_INTERVAL_MS = 600;
const HISTORY_KEY = 'terminal_history';
const MAX_HISTORY = 100;

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

function loadHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveHistory(history: string[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-MAX_HISTORY)));
  } catch {}
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

function isTerminated(status: TerminalCommand['status']): boolean {
  return status === 'completed' || status === 'error';
}

// ──────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────

const STYLES = {
  wrapper: {
    background: 'var(--bg-card-solid)',
    borderRadius: 12,
    border: '1px solid var(--border)',
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', 'Courier New', monospace",
    fontSize: 13,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: 400,
  },
  header: {
    background: 'var(--bg)',
    borderBottom: '1px solid var(--border)',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  dot: (color: string) => ({
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color,
  }),
  title: {
    flex: 1,
    fontSize: 12,
    color: 'var(--text-muted)',
    fontFamily: 'sans-serif',
    userSelect: 'none' as const,
  },
  btnIcon: {
    background: 'none',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 11,
    padding: '3px 8px',
    lineHeight: 1.4,
    transition: 'color 0.15s, border-color 0.15s',
  },
  body: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '12px 16px',
    minHeight: 0,
  },
  commandBlock: {
    marginBottom: 16,
  },
  promptLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap' as const,
  },
  promptSymbol: {
    color: 'var(--success)',
    fontWeight: 700,
    flexShrink: 0,
  },
  commandText: {
    color: 'var(--text)',
    wordBreak: 'break-all' as const,
    flex: 1,
  },
  timestamp: {
    color: 'var(--text-muted)',
    fontSize: 10,
    fontFamily: 'sans-serif',
    flexShrink: 0,
  },
  outputPre: (hasError: boolean) => ({
    margin: '4px 0 4px 20px',
    padding: '8px 12px',
    background: '#010409',
    borderRadius: 6,
    border: `1px solid ${hasError ? 'var(--danger)30' : 'var(--bg-card-solid)'}`,
    color: hasError ? 'var(--danger)' : 'var(--text)',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    lineHeight: 1.6,
    overflowX: 'auto' as const,
    fontSize: 12,
  }),
  spinner: {
    display: 'inline-block',
    width: 10,
    height: 10,
    border: '2px solid var(--success)40',
    borderTop: '2px solid var(--success)',
    borderRadius: '50%',
    animation: 'termSpin 0.8s linear infinite',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  statusBadge: (status: TerminalCommand['status']) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 10,
    fontFamily: 'sans-serif',
    padding: '1px 6px',
    borderRadius: 4,
    background:
      status === 'completed' ? '#1a4731' :
      status === 'error' ? '#4d1f1f' :
      status === 'running' ? '#162032' :
      '#1c2128',
    color:
      status === 'completed' ? 'var(--success)' :
      status === 'error' ? 'var(--danger)' :
      status === 'running' ? 'var(--primary)' :
      'var(--text-muted)',
  }),
  inputArea: {
    background: 'var(--bg)',
    borderTop: '1px solid var(--border)',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  promptDollar: {
    color: 'var(--success)',
    fontWeight: 700,
    flexShrink: 0,
    userSelect: 'none' as const,
  },
  input: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'var(--text)',
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: 1.5,
  },
  sendBtn: {
    background: 'var(--success)',
    border: 'none',
    borderRadius: 6,
    color: T.text,
    cursor: 'pointer',
    fontSize: 12,
    padding: '5px 12px',
    fontWeight: 600,
    flexShrink: 0,
    transition: 'background 0.15s',
  },
  offlineBanner: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 12,
    color: 'var(--text-muted)',
    padding: 32,
    textAlign: 'center' as const,
    fontFamily: 'sans-serif',
  },
  copyBtn: {
    background: 'none',
    border: '1px solid var(--border)',
    borderRadius: 4,
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 10,
    padding: '2px 6px',
    marginLeft: 8,
    transition: 'color 0.15s',
  },
  exitCode: (code?: number) => ({
    fontSize: 10,
    fontFamily: 'sans-serif',
    color: code === 0 ? 'var(--success)' : 'var(--danger)',
    marginLeft: 8,
  }),
  emptyMsg: {
    color: 'var(--text-muted)',
    fontSize: 12,
    fontFamily: 'sans-serif',
    padding: '8px 0',
  },
} as const;

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────

export function Terminal({ runnerId, isOnline }: { runnerId: string; isOnline: boolean }) {
  const [commands, setCommands] = useState<TerminalCommand[]>([]);
  const [input, setInput] = useState('');
  const [historyList, setHistoryList] = useState<string[]>(() => loadHistory());
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isRunning, setIsRunning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runningCmdRef = useRef<string | null>(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  // Load history of commands from backend on mount
  useEffect(() => {
    if (!isOnline) return;
    api.get<any>(`/runner-v3/${runnerId}/terminal/commands?limit=15`)
      .then((data) => {
        const cmds: TerminalCommand[] = data?.data?.commands || data?.commands || [];
        setCommands(cmds.slice(-15));
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {});
  }, [runnerId, isOnline]);

  // Poll active command output
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback((cmdId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    runningCmdRef.current = cmdId;

    pollRef.current = setInterval(async () => {
      if (!runningCmdRef.current) return;
      try {
        const data = await api.get<any>(`/runner-v3/${runnerId}/terminal/output/${runningCmdRef.current}`);
        const updated: TerminalCommand = data?.data || data;
        setCommands((prev) =>
          prev.map((c) => c.id === updated.id ? updated : c)
        );
        scrollToBottom();

        if (isTerminated(updated.status)) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          runningCmdRef.current = null;
          setIsRunning(false);
        }
      } catch {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        runningCmdRef.current = null;
        setIsRunning(false);
      }
    }, POLL_INTERVAL_MS);
  }, [runnerId, scrollToBottom]);

  const handleSubmit = useCallback(async () => {
    const cmd = input.trim();
    if (!cmd || isRunning || !isOnline) return;

    setInput('');
    setHistoryIdx(-1);

    // Add to history
    const newHistory = [...historyList.filter((h) => h !== cmd), cmd];
    setHistoryList(newHistory);
    saveHistory(newHistory);

    // Optimistic: add pending command
    const optimistic: TerminalCommand = {
      id: `tmp_${Date.now()}`,
      command: cmd,
      status: 'pending',
      output: '',
      created_at: new Date().toISOString(),
    };
    setCommands((prev) => [...prev, optimistic]);
    setIsRunning(true);
    setTimeout(scrollToBottom, 50);

    try {
      const raw = await api.post<any>(`/runner-v3/${runnerId}/terminal/exec`, { command: cmd });
      const res = raw?.data || raw;
      const created: TerminalCommand = {
        id: res.id,
        command: cmd,
        status: res.status || 'pending',
        output: '',
        created_at: optimistic.created_at,
      };
      // Replace optimistic with real
      setCommands((prev) => prev.map((c) => c.id === optimistic.id ? created : c));
      startPolling(res.id);
    } catch (err: any) {
      // Mark as error
      setCommands((prev) => prev.map((c) =>
        c.id === optimistic.id
          ? { ...c, status: 'error', output: `[Erro: ${err?.message || 'Falha ao enviar comando'}]` }
          : c
      ));
      setIsRunning(false);
    }
  }, [input, isRunning, isOnline, runnerId, historyList, startPolling, scrollToBottom]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
      return;
    }
    // Shift+Enter = new line (default textarea behavior, no action needed)
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyList.length === 0) return;
      const newIdx = Math.min(historyIdx + 1, historyList.length - 1);
      setHistoryIdx(newIdx);
      setInput(historyList[historyList.length - 1 - newIdx] ?? '');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIdx <= 0) {
        setHistoryIdx(-1);
        setInput('');
        return;
      }
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      setInput(historyList[historyList.length - 1 - newIdx] ?? '');
      return;
    }
    if (e.key === 'c' && e.ctrlKey) {
      if (runningCmdRef.current) {
        // Just stop polling (no cancel endpoint, runner will timeout)
        clearInterval(pollRef.current!);
        pollRef.current = null;
        const cmdId = runningCmdRef.current;
        runningCmdRef.current = null;
        setIsRunning(false);
        setCommands((prev) => prev.map((c) =>
          c.id === cmdId && !isTerminated(c.status)
            ? { ...c, status: 'error', output: c.output + '\n[Interrompido pelo usuário]' }
            : c
        ));
      }
    }
    if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setCommands([]);
    }
  }, [historyList, historyIdx, handleSubmit]);

  const handleCopy = useCallback((text: string, id: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  }, []);

  const handleClear = useCallback(() => setCommands([]), []);
  const handleFullscreen = useCallback(() => setIsFullscreen((f) => !f), []);

  // Click on terminal area focuses input
  const handleBodyClick = useCallback(() => { inputRef.current?.focus(); }, []);

  const wrapperStyle = {
    ...STYLES.wrapper,
    ...(isFullscreen ? {
      position: 'fixed' as const,
      inset: 0,
      zIndex: 9000,
      borderRadius: 0,
      height: '100vh',
    } : {
      maxHeight: 520,
    }),
  };

  return (
    <div style={wrapperStyle}>
      <style>{`
        @keyframes termSpin { to { transform: rotate(360deg); } }
        .term-copy-btn:hover { color: var(--text) !important; border-color: var(--text-muted) !important; }
        .term-icon-btn:hover { color: var(--text) !important; border-color: var(--text-muted) !important; }
        .term-send-btn:hover { background: #2ea043 !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={STYLES.header}>
        <div style={STYLES.dot('#ff5f57')} />
        <div style={STYLES.dot('#ffbd2e')} />
        <div style={STYLES.dot('#28ca41')} />
        <span style={STYLES.title}>
          Terminal Remoto
          {isOnline
            ? <span style={{ color: 'var(--success)', marginLeft: 8 }}>● online</span>
            : <span style={{ color: 'var(--danger)', marginLeft: 8 }}>● offline</span>
          }
        </span>
        <Button variant="ghost"
          class="term-icon-btn"
          style={STYLES.btnIcon}
          onClick={handleClear}
          title="Limpar terminal"
 >Limpar</Button>
        <Button variant="ghost"
          class="term-icon-btn"
          style={STYLES.btnIcon}
          onClick={handleFullscreen}
          title={isFullscreen ? 'Sair do fullscreen' : 'Fullscreen'}
        >{isFullscreen ? '⊡' : '⤢'}</Button>
      </div>

      {/* ── Body ── */}
      {!isOnline ? (
        <div style={STYLES.offlineBanner}>
 <span style={{ fontSize: 40 }}></span>
          <p style={{ fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Máquina Offline</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
            O terminal fica disponível quando a máquina está conectada e online.
          </p>
        </div>
      ) : (
        <div
          ref={bodyRef}
          style={STYLES.body}
          onClick={handleBodyClick}
        >
          {commands.length === 0 && (
            <p style={STYLES.emptyMsg}>
              Nenhum comando executado ainda. Digite um comando abaixo e pressione Enter. Use Shift+Enter para múltiplas linhas.
            </p>
          )}

          {commands.map((cmd) => (
            <div key={cmd.id} style={STYLES.commandBlock}>
              {/* Prompt line */}
              <div style={STYLES.promptLine}>
                <span style={STYLES.promptSymbol}>$</span>
                <span style={STYLES.commandText}>{cmd.command}</span>
                <span style={STYLES.timestamp}>{formatTimestamp(cmd.created_at)}</span>
                <span style={STYLES.statusBadge(cmd.status)}>
                  {cmd.status === 'running' && <span style={STYLES.spinner} />}
 {cmd.status === 'pending' && ' '}
                  {cmd.status === 'completed' && ''}
 {cmd.status === 'error' && ' '}
                  {cmd.status === 'running' ? 'executando' :
                   cmd.status === 'pending' ? 'aguardando' :
                   cmd.status === 'completed' ? 'concluído' : 'erro'}
                </span>
                {isTerminated(cmd.status) && cmd.exit_code !== undefined && (
                  <span style={STYLES.exitCode(cmd.exit_code)}>
                    exit {cmd.exit_code}
                  </span>
                )}
              </div>

              {/* Output */}
              {(cmd.output || cmd.status === 'running') && (
                <div style={{ position: 'relative' }}>
                  <pre style={STYLES.outputPre(cmd.status === 'error' && !cmd.output.includes('\n'))}>
                    {cmd.output || (cmd.status === 'running' ? '...' : '')}
                    {cmd.status === 'running' && (
                      <span style={{ ...STYLES.spinner, marginLeft: 4 }} />
                    )}
                  </pre>
                  {cmd.output && (
                    <Button variant="ghost"
                      class="term-copy-btn"
                      style={{ ...STYLES.copyBtn, position: 'absolute' as const, top: 6, right: 6 }}
                      onClick={(e) => { e.stopPropagation(); handleCopy(cmd.output, cmd.id); }}
 >{copiedId === cmd.id ? 'copiado' : ' copiar'}</Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Input Area ── */}
      {isOnline && (
        <div style={STYLES.inputArea}>
          <span style={STYLES.promptDollar}>$</span>
          <Textarea
            ref={inputRef}
            value={input}
            placeholder={isRunning ? 'Aguardando resposta...' : 'Digite um comando... (Shift+Enter para nova linha)'}
            disabled={isRunning}
            rows={Math.min(5, Math.max(1, input.split('\n').length))}
            style={{
              ...STYLES.input,
              opacity: isRunning ? 0.5 : 1,
              cursor: isRunning ? 'not-allowed' : 'text',
              resize: 'none',
              minHeight: 36,
              maxHeight: 120,
              lineHeight: '1.4',
              paddingTop: 8,
              paddingBottom: 8,
            }}
            onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
            onKeyDown={handleKeyDown as any}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <Button variant="ghost"
            class="term-send-btn"
            style={{
              ...STYLES.sendBtn,
              opacity: isRunning || !input.trim() ? 0.5 : 1,
              cursor: isRunning || !input.trim() ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={isRunning || !input.trim()}
          >
 {isRunning ? '' : 'Enviar'}
          </Button>
        </div>
      )}
    </div>
  );
}
